import React, { useState, useEffect, useRef } from 'react';
import { X, Search, MessageSquare, Archive, ArchiveRestore, Trash2, Edit3, Check, CheckSquare, Square, Lock, Download } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const ConversationDrawer = ({
  isOpen,
  onClose,
  sessions,         // toutes les sessions (incluant archivées)
  activeSessionId,
  onSelectSession,
  onArchiveSession,
  onUnarchiveSession,
  onDeleteSession,
  onRenameSession,
  onNewSession,
  onExportZip,      // (sessionIds: number[]) => Promise<void>  — export markdown bundle
}) => {
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('active');  // 'all' | 'active' | 'archived'
  const [memoryFilter, setMemoryFilter] = useState('all'); // 'all' | 'persistent' | 'ephemeral' (S50)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const editRef = useRef(null);
  const drawerRef = useRef(null);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIds(new Set());
      setEditingId(null);
    }
  }, [isOpen]);

  // Focus rename input
  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay to avoid immediate close on trigger click
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClick); };
  }, [isOpen, onClose]);

  // Escape pour fermer
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const isEphemeral = (s) => s.memory_scope === 'ephemeral';

  const filteredSessions = sessions
    .filter(s => {
      if (filter === 'active') return !s.archived_at;
      if (filter === 'archived') return !!s.archived_at;
      return true;
    })
    .filter(s => {
      if (memoryFilter === 'persistent') return !isEphemeral(s);
      if (memoryFilter === 'ephemeral') return isEphemeral(s);
      return true;
    })
    .filter(s => {
      if (!searchQuery.trim()) return true;
      return (s.title || '').toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  const activeSessions = sessions.filter(s => !s.archived_at);
  const archivedSessions = sessions.filter(s => !!s.archived_at);
  const ephemeralCount = sessions.filter(isEphemeral).length;

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredSessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSessions.map(s => s.id)));
    }
  };

  const startRename = (session) => {
    setEditingId(session.id);
    setEditTitle(session.title || '');
  };

  const submitRename = () => {
    if (editingId && editTitle.trim()) {
      onRenameSession(editingId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  };

  const handleBatchArchive = async () => {
    if (selectedIds.size === 0) return;
    for (const id of selectedIds) {
      const session = sessions.find(s => s.id === id);
      if (session && !session.archived_at) {
        await onArchiveSession(id);
      }
    }
    setSelectedIds(new Set());
    toast.success(`${selectedIds.size} conversation(s) archivée(s)`);
  };

  const handleBatchExportZip = async () => {
    if (selectedIds.size === 0 || exporting || !onExportZip) return;
    setExporting(true);
    try {
      await onExportZip([...selectedIds]);
      // Garder la sélection pour permettre d'enchaîner Archiver/Supprimer
    } catch (e) {
      toast.error('Export impossible : ' + (e?.message || 'erreur'));
    } finally {
      setExporting(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirmModal(`Supprimer ${selectedIds.size} conversation(s) ? Cette action est irréversible.`, { danger: true });
    if (!ok) return;
    for (const id of selectedIds) {
      await onDeleteSession(id);
    }
    setSelectedIds(new Set());
    toast.success(`${selectedIds.size} conversation(s) supprimée(s)`);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' à ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 998,
        background: 'rgba(0,0,0,0.35)',
        backdropFilter: 'blur(2px)',
        animation: 'drawerFadeIn 0.2s ease',
      }} />

      {/* Drawer */}
      <div
        ref={drawerRef}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: '360px', maxWidth: '90vw',
          zIndex: 999,
          background: 'var(--modal-bg, var(--card-bg))',
          borderLeft: '1px solid var(--card-border)',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column',
          animation: 'drawerSlideIn 0.25s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 18px', borderBottom: '1px solid var(--card-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>
              Conversations
            </h3>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              {activeSessions.length} active{activeSessions.length !== 1 ? 's' : ''}
              {archivedSessions.length > 0 && ` · ${archivedSessions.length} archivée${archivedSessions.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary)', padding: '4px', borderRadius: '6px',
              display: 'flex', alignItems: 'center',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* Recherche */}
        <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--card-border)' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 10px', borderRadius: '8px',
            background: 'var(--input-bg)', border: '1px solid var(--input-border)',
          }}>
            <Search size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent', fontSize: '12px',
                color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Filtres état */}
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            {[
              { key: 'all', label: `Toutes (${sessions.length})` },
              { key: 'active', label: `Actives (${activeSessions.length})` },
              { key: 'archived', label: `Archivées (${archivedSessions.length})` },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  padding: '3px 10px', borderRadius: '12px',
                  border: filter === f.key ? '1px solid var(--accent)' : '1px solid var(--card-border)',
                  background: filter === f.key ? 'rgba(37,99,235,0.1)' : 'transparent',
                  color: filter === f.key ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '11px', fontWeight: filter === f.key ? '600' : '400',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Filtres mémoire (S50) — affiché uniquement si au moins une session ephemeral */}
          {ephemeralCount > 0 && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'Mémoire : toutes' },
                { key: 'persistent', label: 'Persistantes' },
                { key: 'ephemeral', label: `Éphémères (${ephemeralCount})`, amber: true },
              ].map(f => {
                const isActive = memoryFilter === f.key;
                const activeColor = f.amber ? '#f59e0b' : 'var(--accent)';
                const activeBg = f.amber ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.1)';
                return (
                  <button
                    key={f.key}
                    onClick={() => setMemoryFilter(f.key)}
                    style={{
                      padding: '3px 10px', borderRadius: '12px',
                      border: isActive ? `1px solid ${activeColor}` : '1px solid var(--card-border)',
                      background: isActive ? activeBg : 'transparent',
                      color: isActive ? activeColor : 'var(--text-secondary)',
                      fontSize: '11px', fontWeight: isActive ? '600' : '400',
                      cursor: 'pointer', transition: 'all 0.15s',
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    {f.amber && <Lock size={10} />} {f.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Barre batch (si sélection) */}
        {selectedIds.size > 0 && (
          <div style={{
            padding: '8px 18px', borderBottom: '1px solid var(--card-border)',
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'rgba(37,99,235,0.05)',
          }}>
            <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--accent)', flex: 1 }}>
              {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}
            </span>
            {onExportZip && (
              <button
                onClick={handleBatchExportZip}
                disabled={exporting}
                title="Télécharger un ZIP markdown — archive personnelle, ne touche pas au serveur ni à la mémoire IA."
                style={{
                  display: 'flex', alignItems: 'center', gap: '3px',
                  padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
                  border: '1px solid rgba(167,139,250,0.3)',
                  background: exporting ? 'rgba(167,139,250,0.08)' : 'rgba(167,139,250,0.12)',
                  color: '#a78bfa', cursor: exporting ? 'wait' : 'pointer',
                  opacity: exporting ? 0.7 : 1,
                }}
              >
                <Download size={11} /> {exporting ? 'Export…' : 'Télécharger'}
              </button>
            )}
            <button
              onClick={handleBatchArchive}
              title="Range les conversations hors du listing actif. Note : l'IA peut encore les retrouver via recherche sémantique. Pour les masquer définitivement de l'IA, utilisez le coffre-fort sur les messages clés."
              style={{
                display: 'flex', alignItems: 'center', gap: '3px',
                padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
                border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                color: 'var(--text-secondary)', cursor: 'pointer',
              }}
            >
              <Archive size={11} /> Archiver
            </button>
            <button
              onClick={handleBatchDelete}
              style={{
                display: 'flex', alignItems: 'center', gap: '3px',
                padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)',
                color: '#ef4444', cursor: 'pointer',
              }}
            >
              <Trash2 size={11} /> Supprimer
            </button>
          </div>
        )}

        {/* Liste */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {/* Toggle tout sélectionner */}
          {filteredSessions.length > 0 && (
            <button
              onClick={toggleAll}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 18px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: '11px', color: 'var(--text-muted)',
                width: '100%',
              }}
            >
              {selectedIds.size === filteredSessions.length ? <CheckSquare size={12} style={{ color: 'var(--accent)' }} /> : <Square size={12} />}
              {selectedIds.size === filteredSessions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          )}

          {filteredSessions.map(session => {
            const isActive = session.id === activeSessionId;
            const isSelected = selectedIds.has(session.id);
            const isArchived = !!session.archived_at;

            return (
              <div
                key={session.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  padding: '10px 18px',
                  background: isActive ? 'rgba(37,99,235,0.06)' : 'transparent',
                  borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                  transition: 'background 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--conv-item-hover, rgba(255,255,255,0.03))'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Checkbox */}
                <span
                  onClick={(e) => { e.stopPropagation(); toggleSelect(session.id); }}
                  style={{ cursor: 'pointer', flexShrink: 0, marginTop: '2px' }}
                >
                  {isSelected
                    ? <CheckSquare size={14} style={{ color: 'var(--accent)' }} />
                    : <Square size={14} style={{ color: 'var(--text-muted)' }} />
                  }
                </span>

                {/* Contenu principal */}
                <div
                  style={{ flex: 1, minWidth: 0 }}
                  onClick={() => {
                    if (!isArchived) {
                      onSelectSession(session.id);
                      onClose();
                    }
                  }}
                >
                  {editingId === session.id ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input
                        ref={editRef}
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') submitRename();
                          if (e.key === 'Escape') { setEditingId(null); setEditTitle(''); }
                        }}
                        onBlur={submitRename}
                        style={{
                          flex: 1, fontSize: '12px', fontWeight: '500',
                          padding: '2px 6px', borderRadius: '4px',
                          border: '1px solid var(--accent)', outline: 'none',
                          background: 'var(--input-bg)', color: 'var(--text)',
                          fontFamily: 'inherit',
                        }}
                      />
                      <Check
                        size={14}
                        style={{ color: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
                        onClick={(e) => { e.stopPropagation(); submitRename(); }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      fontSize: '12px', fontWeight: isActive ? '600' : '500',
                      color: isArchived ? 'var(--text-muted)' : 'var(--text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textDecoration: isArchived ? 'line-through' : 'none',
                    }}>
                      {session.title || 'Sans titre'}
                      {isArchived && (
                        <span style={{
                          marginLeft: '6px', fontSize: '9px', padding: '1px 5px',
                          borderRadius: '8px', background: 'rgba(245,158,11,0.1)',
                          color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)',
                          fontWeight: '600', textDecoration: 'none',
                          display: 'inline-block',
                        }}>
                          archivée
                        </span>
                      )}
                      {isEphemeral(session) && (
                        <span
                          title="Cette conversation est hors mémoire : rien n'est indexé en mémoire persistante"
                          style={{
                            marginLeft: '6px', fontSize: '9px', padding: '1px 5px',
                            borderRadius: '8px', background: 'rgba(245,158,11,0.12)',
                            color: '#f59e0b', border: '1px solid rgba(245,158,11,0.28)',
                            fontWeight: '600', textDecoration: 'none',
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                          }}
                        >
                          <Lock size={8} /> Hors mémoire
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '8px', marginTop: '3px', fontSize: '10px', color: 'var(--text-muted)' }}>
                    <span>{session.messages_count || 0} msg</span>
                    <span>{formatDate(session.updated_at || session.created_at)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginTop: '2px' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); startRename(session); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-muted)', borderRadius: '4px' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Renommer"
                  >
                    <Edit3 size={12} />
                  </button>

                  {isArchived ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); onUnarchiveSession(session.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-muted)', borderRadius: '4px' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#10b981'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                      title="Désarchiver"
                    >
                      <ArchiveRestore size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); onArchiveSession(session.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-muted)', borderRadius: '4px' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f59e0b'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                      title="Archiver"
                    >
                      <Archive size={12} />
                    </button>
                  )}

                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const ok = await confirmModal('Supprimer cette conversation ? Les messages seront perdus.', { danger: true });
                      if (ok) onDeleteSession(session.id);
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-muted)', borderRadius: '4px' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Supprimer"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredSessions.length === 0 && (
            <div style={{ padding: '24px 18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
              {searchQuery ? 'Aucun résultat.' : 'Aucune conversation.'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--card-border)',
          display: 'flex', justifyContent: 'center',
        }}>
          <button
            onClick={() => { onNewSession(); onClose(); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 20px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              border: 'none', color: 'white',
              fontSize: '12px', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.2s',
              boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            Nouvelle conversation
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes drawerSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes drawerFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ConversationDrawer;
